import { Box, List, ListItem, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { GenCurrency } from './GenDataFormat';

const PREFIX = 'ServicesList';

const classes = {
  ul: `${PREFIX}-ul`,
  listMediumUp: `${PREFIX}-listMediumUp`,
  li: `${PREFIX}-li`,
  liDetail: `${PREFIX}-liDetail`,
  listIcon: `${PREFIX}-listIcon`,
  icon: `${PREFIX}-icon`,
  primaryText: `${PREFIX}-primaryText`,
  secondaryText: `${PREFIX}-secondaryText`,
  listHeader: `${PREFIX}-listHeader`,
  headerText: `${PREFIX}-headerText`,
  name: `${PREFIX}-name`,
  price: `${PREFIX}-price`,
  completed: `${PREFIX}-completed`,
  ordered: `${PREFIX}-ordered`,
  type: `${PREFIX}-type`,
};

const StyledList = styled(List)(({ theme }) => ({
  [`& .${classes.ul}`]: {
    padding: theme.spacing(0),
  },

  [`& .${classes.listMediumUp}`]: {
    display: 'block',
    overflowX: 'scroll',
    height: '100px',
    padding: theme.spacing(0, 2, 0, 0),
  },

  [`& .${classes.li}`]: {
    padding: theme.spacing(0),
    marginBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  [`& .${classes.liDetail}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.up('lg')]: {
      border: `1px solid ${theme.palette.divider}`,
      borderTop: 'none',
    },
  },

  [`& .${classes.listIcon}`]: {
    minWidth: '0px',
    width: '0px',
    margin: ` 0px ${theme.spacing(1)}`,
  },

  [`& .${classes.icon}`]: {
    fontSize: '5px',
    position: 'relative',
    left: '-5px',
    color: theme.palette.text.secondary,
  },

  [`& .${classes.primaryText}`]: {
    fontSize: theme.typography.subtitle2.fontSize,
    color: theme.palette.text.primary,
  },

  [`& .${classes.secondaryText}`]: {
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.secondary,
  },

  [`& .${classes.listHeader}`]: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(0),
    [theme.breakpoints.up('lg')]: {
      border: `1px solid ${theme.palette.divider}`,
    },
  },

  [`& .${classes.headerText}`]: {
    fontSize: theme.typography.body1.fontSize,
    color: theme.palette.text.primary,
  },

  [`& .${classes.name}`]: {
    flexBasis: '264px',
  },

  [`& .${classes.price}`]: {
    flexBasis: '60px',
  },

  [`& .${classes.completed}`]: {
    flexBasis: '102px',
  },

  [`& .${classes.ordered}`]: {
    flexBasis: '81px',
  },

  [`& .${classes.type}`]: {
    flexBasis: '112px',
  },
}));

const ListItemCustom = ({ service, price, detail }) => {
  const smallScreen = useMediaQuery(theme => theme.breakpoints.up('sm'));
  const mediumScreen = useMediaQuery(theme => theme.breakpoints.up('md'));
  const largeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const getServiceListCompletedClass = () => {
    return detail ? classes.completed : null;
  };
  return (
    <ListItem className={`${detail ? classes.liDetail : classes.li}`}>
      <Box className={`${detail ? classes.name : null}`}>
        <Typography className={classes.primaryText}>{service}</Typography>
      </Box>
      <Box className={`${detail ? classes.price : null}`}>
        <Typography className={classes.secondaryText}>
          <GenCurrency value={price} />
        </Typography>
      </Box>
      {(smallScreen && !mediumScreen) || (detail && mediumScreen) ? (
        <Box className={getServiceListCompletedClass()}>
          <Typography className={classes.secondaryText}></Typography>
        </Box>
      ) : null}
      {largeScreen && detail ? (
        <>
          <Box className={classes.ordered}>
            <Typography className={classes.secondaryText}></Typography>
          </Box>
          <Box className={classes.type}>
            <Typography className={classes.secondaryText}></Typography>
          </Box>
        </>
      ) : null}
    </ListItem>
  );
};

const ServicesList = ({ dense = false, services, detail }) => {
  const { t } = useTranslation('workOrdersTable');
  const smallScreen = useMediaQuery(theme => theme.breakpoints.up('sm'));
  const mediumScreen = useMediaQuery(theme => theme.breakpoints.up('md'));
  const largeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const getServiceListDetailFooter = () => {
    if (largeScreen)
      return (
        <>
          <Box className={classes.ordered}>
            <Typography className={classes.headerText}>Ordered by</Typography>
          </Box>
          <Box className={classes.type}>
            <Typography className={classes.headerText}>
              Type of service
            </Typography>
          </Box>
        </>
      );
    if (smallScreen)
      return (
        <Box className={classes.completed}>
          <Typography className={classes.headerText}>Completed by</Typography>
        </Box>
      );
    return null;
  };

  return (
    <StyledList
      dense={dense}
      className={`${
        !detail && mediumScreen && services.length > 3
          ? classes.listMediumUp
          : classes.ul
      }`}
    >
      {detail ? (
        <ListItem className={`${classes.li} ${classes.listHeader}`}>
          <Box className={classes.name}>
            <Typography className={classes.headerText}>
              {t('detail.servicesTable.servicesColumnLabels.nameLabel')}
            </Typography>
          </Box>
          <Box className={classes.price}>
            <Typography className={classes.headerText}>
              {t('detail.servicesTable.servicesColumnLabels.priceLabel')}
            </Typography>
          </Box>
          {getServiceListDetailFooter()}
        </ListItem>
      ) : null}
      {services.map((service, i) => (
        <ListItemCustom
          key={i}
          service={service.name}
          price={service.price}
          detail={detail}
        />
      ))}
    </StyledList>
  );
};

export default ServicesList;
